import apiClient from './client'
import type { User } from '../types/user.types'
import type { ConfirmationChannel } from '../types/user.types'

interface LoginResponse {
    token: string
    user: User
}

export interface RegisterResponse {
    requiresConfirmation: boolean
    channel: ConfirmationChannel
    destinationHint: string
    message: string
    telegramDeepLink?: string | null
    needsEmailConfirmation?: boolean
    needsTelegramVerification?: boolean
    authResponse?: LoginResponse
    email?: string
    fullName?: string
    provider?: string
}

interface SocialStartResponse {
    provider: string
    authorizationUrl: string
    state: string
}

export interface RegistrationStatusResponse {
    emailVerified: boolean
    phoneVerified: boolean
    enabled: boolean
    telegramDeepLink?: string | null
    billNotificationChannel?: string | null
}

export type LoginLookupStatus =
    | 'NOT_FOUND'
    | 'PASSWORD_REQUIRED'
    | 'ACTIVATION_REQUIRED'
    | 'SOCIAL_SIGN_IN'

export interface LoginLookupResponse {
    status: LoginLookupStatus
    channel?: ConfirmationChannel
    destinationHint?: string
    provider?: string
    message?: string
    botDeepLink?: string | null
}

export interface ActivationInitiatedResponse {
    channel: ConfirmationChannel
    destinationHint: string
    message: string
    botDeepLink?: string | null
}

export interface PasswordResetInitiatedResponse {
    channel?: ConfirmationChannel | null
    destinationHint?: string | null
    message: string
}

export interface TelegramLinkResponse {
    deepLink: string
    botUsername: string
}

export const login = async (emailOrPhone: string, password: string): Promise<LoginResponse> => {
    const response = await apiClient.post('/auth/login', { emailOrPhone, password })
    return response.data.data
}

export const loginLookup = async (emailOrPhone: string): Promise<LoginLookupResponse> => {
    const response = await apiClient.post('/auth/login/lookup', { emailOrPhone })
    return response.data.data
}

export const startActivation = async (emailOrPhone: string): Promise<ActivationInitiatedResponse> => {
    const response = await apiClient.post('/auth/activation/start', { emailOrPhone })
    return response.data.data
}

export const resendActivation = async (emailOrPhone: string): Promise<ActivationInitiatedResponse> => {
    const response = await apiClient.post('/auth/activation/resend', { emailOrPhone })
    return response.data.data
}

export const confirmActivation = async (payload: {
    token: string
    password: string
    confirmPassword: string
    birthDate?: string
    photoUrl?: string
}): Promise<LoginResponse> => {
    const response = await apiClient.post('/auth/activation/confirm', payload)
    return response.data.data
}

export const register = async (payload: {
    email?: string | null
    phone?: string | null
    password: string
    fullName: string
    confirmationChannel?: ConfirmationChannel
    registrationPath?: 'EMAIL' | 'PHONE'
    personalDataConsent?: boolean
    locale?: 'en' | 'ua'
    telegramInviteToken?: string
}): Promise<RegisterResponse> => {
    const response = await apiClient.post('/auth/register', payload)
    return response.data.data
}

export const getTelegramRegisterInvite = async (token: string): Promise<{ phone: string; fullName?: string | null }> => {
    const response = await apiClient.get('/auth/telegram/register-invite', {
        params: { token },
    })
    return response.data.data
}

export const resendRegistration = async (payload: {
    email?: string
    phone?: string
}): Promise<RegisterResponse> => {
    const response = await apiClient.post('/auth/register/resend', payload)
    return response.data.data
}

export const socialStart = async (
    provider: 'google' | 'apple',
    redirectUri: string
): Promise<SocialStartResponse> => {
    const response = await apiClient.get(`/auth/social/${provider}/start`, {
        params: { redirectUri },
    })
    return response.data.data
}

export const socialRegister = async (payload: {
    provider: 'google' | 'apple'
    authorizationCode: string
    redirectUri: string
    locale?: 'en' | 'ua'
}): Promise<RegisterResponse> => {
    const response = await apiClient.post('/auth/social/register', payload)
    return response.data.data
}

export const socialLogin = async (payload: {
    provider: 'google' | 'apple'
    authorizationCode: string
    redirectUri: string
}): Promise<LoginResponse> => {
    const response = await apiClient.post('/auth/social/login', payload)
    return response.data.data
}

export const confirmRegistration = async (token: string): Promise<LoginResponse> => {
    const response = await apiClient.post('/auth/confirm', { token })
    return response.data.data
}

export const getRegistrationStatus = async (): Promise<RegistrationStatusResponse> => {
    const response = await apiClient.get('/auth/registration/status')
    return response.data.data
}

export const getTelegramLink = async (): Promise<TelegramLinkResponse> => {
    const response = await apiClient.get('/auth/telegram/link')
    return response.data.data
}

export const getCurrentUser = async (): Promise<User> => {
    const response = await apiClient.get('/auth/me')
    return response.data.data
}

export const completeProfile = async (payload: {
    fullName?: string
    email?: string
    birthDate?: string
    photoUrl?: string
    phone?: string
}): Promise<User> => {
    const response = await apiClient.put('/auth/profile', payload)
    return response.data.data
}

export const sendPhoneConfirmation = async (
    channel: ConfirmationChannel
): Promise<RegisterResponse> => {
    const response = await apiClient.post('/auth/phone/confirmation', undefined, {
        params: { channel },
    })
    return response.data.data
}

export const forgotPassword = async (
    emailOrPhone: string
): Promise<PasswordResetInitiatedResponse> => {
    const response = await apiClient.post('/auth/forgot-password', { emailOrPhone })
    return response.data.data
}

export const confirmPasswordReset = async (payload: {
    token: string
    password: string
    confirmPassword: string
}): Promise<void> => {
    await apiClient.post('/auth/password-reset/confirm', payload)
}

export const changePassword = async (payload: {
    currentPassword: string
    newPassword: string
    confirmPassword: string
}): Promise<void> => {
    await apiClient.put('/users/me/password', payload)
}
